import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Users, BookOpen, ClipboardCheck, ListTodo, TrendingUp, Plus, Trash2, Settings } from 'lucide-react';
import { toast } from 'sonner';

const DEPT_COLORS = { FOH: '#3B82F6', BOH: '#F97316' };

function StatCard({ label, foh, boh, suffix = '' }) {
  return (
    <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm">
      <CardContent className="p-4">
        <p className="text-xs text-stone-500 font-medium mb-2">{label}</p>
        <div className="flex items-end justify-between gap-4">
          <div>
            <Badge className="bg-blue-100 text-blue-700 text-[10px] mb-1">FOH</Badge>
            <p className="text-xl font-bold text-stone-900">{foh}{suffix}</p>
          </div>
          <div className="text-right">
            <Badge className="bg-orange-100 text-orange-700 text-[10px] mb-1">BOH</Badge>
            <p className="text-xl font-bold text-stone-900">{boh}{suffix}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PositionManager({ api, isAdmin }) {
  const [positions, setPositions] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDept, setNewDept] = useState('FOH');

  const load = useCallback(async () => {
    try {
      const { data } = await api('get', '/positions');
      setPositions(data);
    } catch { /* positions load silently */ }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const addPosition = async () => {
    if (!newName.trim()) return;
    try {
      await api('post', '/positions', { name: newName.trim(), department: newDept });
      toast.success(`Position "${newName}" added to ${newDept}`);
      setNewName('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add position');
    }
  };

  const deletePosition = async (pos) => {
    if (!window.confirm(`Delete position "${pos.name}" from ${pos.department}?`)) return;
    try {
      await api('delete', `/positions/${pos.id}`);
      toast.success(`Position "${pos.name}" deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete position');
    }
  };

  const fohPositions = positions.filter(p => p.department === 'FOH');
  const bohPositions = positions.filter(p => p.department === 'BOH');

  return (
    <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="position-manager">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-[#D9480F]" />
          <h3 className="text-base font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Manage Positions
          </h3>
        </div>
        {isAdmin && (
          <div className="flex items-end gap-2 mb-4">
            <div className="flex-1">
              <Label className="text-xs text-stone-500">New Position Name</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Expo"
                className="rounded-xl mt-1" data-testid="new-position-name"
                onKeyDown={e => e.key === 'Enter' && addPosition()} />
            </div>
            <div className="w-28">
              <Label className="text-xs text-stone-500">Department</Label>
              <Select value={newDept} onValueChange={setNewDept}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="new-position-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FOH">FOH</SelectItem>
                  <SelectItem value="BOH">BOH</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addPosition} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658] shrink-0" data-testid="add-position-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[{ dept: 'FOH', items: fohPositions, color: 'blue' }, { dept: 'BOH', items: bohPositions, color: 'orange' }].map(({ dept, items, color }) => (
            <div key={dept}>
              <Badge className={`bg-${color}-100 text-${color}-700 mb-2`}>{dept} Positions</Badge>
              <div className="space-y-1.5">
                {items.map(pos => (
                  <div key={pos.id} className="flex items-center justify-between p-2 rounded-lg bg-stone-50 border border-stone-100" data-testid={`position-${pos.id}`}>
                    <span className="text-sm text-stone-700">{pos.name}</span>
                    <div className="flex items-center gap-1">
                      {pos.is_default && <Badge className="bg-stone-100 text-stone-500 text-[10px]">Default</Badge>}
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => deletePosition(pos)}
                          className="text-red-400 hover:text-red-600 p-1 h-6 w-6" data-testid={`delete-position-${pos.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {items.length === 0 && <p className="text-xs text-stone-400 py-2">No positions defined</p>}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DepartmentTab() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    try {
      const { data: d } = await api('get', '/analytics/departments');
      setData(d);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Dept analytics:', err);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full" /></div>;
  }

  const foh = data.FOH || {};
  const boh = data.BOH || {};

  const barData = [
    { name: 'Completion Rate', FOH: foh.completion_rate, BOH: boh.completion_rate },
    { name: 'Test Pass Rate', FOH: foh.test_pass_rate, BOH: boh.test_pass_rate },
    { name: 'Avg Test Score', FOH: foh.avg_test_score, BOH: boh.avg_test_score },
    { name: 'Task Rate', FOH: foh.task_completion_rate, BOH: boh.task_completion_rate },
  ];

  const noData = foh.total_users === 0 && boh.total_users === 0;

  return (
    <div className="space-y-4" data-testid="department-tab">
      {/* Position Manager — always visible */}
      <PositionManager api={api} isAdmin={isAdmin} />

      {noData ? (
        <Card className="bg-white rounded-2xl border border-stone-200">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No department data yet. Assign departments to users to see comparison.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-1">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-500" /><span className="text-xs font-medium text-stone-600">FOH ({foh.total_users} staff)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-orange-500" /><span className="text-xs font-medium text-stone-600">BOH ({boh.total_users} staff)</span></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Team Size" foh={foh.total_users} boh={boh.total_users} />
            <StatCard label="Modules Completed" foh={foh.modules_completed} boh={boh.modules_completed} />
            <StatCard label="Test Pass Rate" foh={foh.test_pass_rate} boh={boh.test_pass_rate} suffix="%" />
            <StatCard label="Tasks Completed" foh={foh.tasks_completed} boh={boh.tasks_completed} />
          </div>

          <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid="dept-comparison-chart">
            <CardContent className="p-5">
              <h3 className="text-base font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                FOH vs BOH Performance (%)
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={barData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#78716C' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#78716C' }} domain={[0, 100]} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                  <Legend />
                  <Bar dataKey="FOH" fill={DEPT_COLORS.FOH} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="BOH" fill={DEPT_COLORS.BOH} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['FOH', 'BOH'].map(dept => {
              const d = dept === 'FOH' ? foh : boh;
              const isFoh = dept === 'FOH';
              const borderClass = isFoh ? 'border-blue-200' : 'border-orange-200';
              const badgeClass = isFoh ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700';
              const barClass = isFoh ? 'bg-blue-500' : 'bg-orange-500';
              return (
                <Card key={dept} className={`rounded-2xl border shadow-sm ${borderClass}`} data-testid={`dept-detail-${dept}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className={`${badgeClass} text-sm px-3 py-1`}>{dept}</Badge>
                      <span className="text-sm text-stone-500">{d.total_users} team member{d.total_users !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-stone-600"><BookOpen className="w-4 h-4" /> Module Completion</div>
                        <span className="text-sm font-bold text-stone-900">{d.completion_rate}%</span>
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-2"><div className={`${barClass} h-2 rounded-full`} style={{ width: `${d.completion_rate}%` }} /></div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-stone-600"><ClipboardCheck className="w-4 h-4" /> Test Pass Rate</div>
                        <span className="text-sm font-bold text-stone-900">{d.test_pass_rate}%</span>
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-2"><div className={`${barClass} h-2 rounded-full`} style={{ width: `${d.test_pass_rate}%` }} /></div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-stone-600"><TrendingUp className="w-4 h-4" /> Avg Test Score</div>
                        <span className="text-sm font-bold text-stone-900">{d.avg_test_score}%</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-stone-600"><ListTodo className="w-4 h-4" /> Task Completion</div>
                        <span className="text-sm font-bold text-stone-900">{d.tasks_completed}/{d.total_tasks_assigned}</span>
                      </div>
                      <div className="w-full bg-stone-100 rounded-full h-2"><div className={`${barClass} h-2 rounded-full`} style={{ width: `${d.task_completion_rate}%` }} /></div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
